// src/pages/team/TeamsPage.jsx
// Extracted from App.js - Teams admin page
// BRIO Migration: Using code "1a" (table default)
// HEADER ALIGNMENT FIX: Full-width header band with BrioFrame inside for centered content
//
// Members column now reflects HFB (HybridFocusBoard) weekly team assignments,
// NOT the static user.teamId field from public.users
//
import React, { useMemo } from 'react';
import { Plus } from 'lucide-react';
import {
  BrioAnalyticsSurface,
  BrioByCode,
  BrioTable,
  BrioTeamIcon,
  BrioIconButton,
  BrioActionGroup,
  BrioFrame,
  getTablePropsFromCode
} from '../../components/BrioComponents';
import { useHFBTeamAssignments } from '../../hooks/useHFBData';


// Presentation code for this page
const PRESENTATION_CODE = '1a';

// Helper: Get current ISO week ID (YYYY-WXX format) matching HFB convention
function getCurrentWeekId() {
  const now = new Date();
  const jan4 = new Date(now.getFullYear(), 0, 4);
  const daysSinceJan4 = Math.floor((now - jan4) / 86400000);
  const weekNum = Math.ceil((daysSinceJan4 + jan4.getDay() + 1) / 7);
  const paddedWeek = String(weekNum).padStart(2, '0');
  return now.getFullYear() + '-W' + paddedWeek;
}

export default function TeamsPage({
  // Data
  teams,
  activeTeams,
  activeUsers,
  activeProjects,
  // Supabase
  useSupabaseDB,
  supabaseTeams,
  supabaseTeamProjects,
  // State setters
  setModal,
  setForm,
  // Current user (for RBAC)
  currentUser,
}) {
  const tableProps = getTablePropsFromCode(PRESENTATION_CODE);

  // RBAC: only admins can create/edit teams
  const canManageTeams = currentUser?.role === 'admin' || currentUser?.role === 'admin-pa';

  // Filter to active users only — inactive users should not appear as supervisors/members
  const activeUsersOnly = useMemo(() => (activeUsers || []).filter(u => u.isActive !== false), [activeUsers]);

  // Get current week ID for HFB assignments
  const currentWeekId = useMemo(() => getCurrentWeekId(), []);

  // Fetch HFB team assignments for current week
  // This is the source of truth for who is assigned to which team
  const { assignments: hfbAssignments, isLoading: hfbLoading } = useHFBTeamAssignments(currentWeekId);

  // Build a map of teamId -> array of user IDs assigned via HFB
  const hfbMembersByTeam = useMemo(() => {
    const map = new Map();
    // Initialize all teams with empty arrays
    activeTeams.forEach(team => {
      map.set(team.id, []);
      map.set(String(team.id), []);
    });
    // Populate from HFB assignments
    (hfbAssignments || []).forEach(assignment => {
      if (assignment.teamId !== null && assignment.teamId !== undefined) {
        const arr = map.get(assignment.teamId) || map.get(String(assignment.teamId));
        if (arr) {
          arr.push(assignment.userId);
        }
      }
    });
    return map;
  }, [hfbAssignments, activeTeams]);

  // Helper to get team members from HFB assignments (active users only)
  const getHfbTeamMembers = (teamId) => {
    const userIds = hfbMembersByTeam.get(teamId) || hfbMembersByTeam.get(String(teamId)) || [];
    return activeUsersOnly.filter(u =>
      userIds.includes(u.id) || userIds.includes(String(u.id))
    );
  };

  // DEBUG: Teams page data sources
  console.log('[TeamsPage] useSupabaseDB=', useSupabaseDB, 'teams=', teams?.length, 'activeTeams=', activeTeams?.length, 'supabaseTeams=', supabaseTeams?.teams?.length);
  console.log('[TeamsPage] HFB weekId=', currentWeekId, 'assignments=', hfbAssignments?.length, 'loading=', hfbLoading);

  return (
    <div className="brio-flex brio-flex-col brio-analytics-font">
      {/* PAGE HEADER BAND: Full-width white background, centered content */}
      <div className="brio-pageband brio-pageband--header">
        <BrioFrame>
          <div className="brio-page-header__content">
            <div className="brio-page-header__left">

              <p className="brio-analytics-muted brio-mt-xs">Manage teams, colors, and supervisors (single source of truth for resource allocation)</p>
            </div>
            <div className="brio-page-header__right">
              {canManageTeams && (
                <button onClick={() => setModal('addTeam')} className="brio-btn brio-btn-primary">
                  <Plus className="w-4 h-4"/>Add Team
                </button>
              )}
            </div>
          </div>
        </BrioFrame>
      </div>

      {/* CONTENT AREA: Table wrapped in BrioFrame for 875px alignment */}
      <BrioFrame>
        <div className="brio-gap-s" style={{ paddingTop: 'var(--brio-space-s)' }}>
          {activeTeams.length > 0 ? (
            <BrioAnalyticsSurface padding="m">
              <BrioByCode code={PRESENTATION_CODE}>
                <BrioTable {...tableProps}>
                  <thead>
                    <tr>
                      <th>team</th>
                      <th>supervisors</th>
                      <th>members</th>
                      <th className="text-center">count</th>
                      {canManageTeams && <th className="text-center col-actions">actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {activeTeams.map(team => {
                      // Get team members from HFB assignments (source of truth)
                      const teamMembers = getHfbTeamMembers(team.id);
                      const supervisors = activeUsersOnly.filter(u => team.supervisorIds?.includes(u.id));

                      // Get linked projects from team_projects join table
                      const linkedProjectIds = supabaseTeamProjects
                        ? supabaseTeamProjects.getProjectIdsForTeam(team.id)
                        : [];
                      const linkedProjects = (activeProjects || []).filter(p =>
                        linkedProjectIds.some(lpId => String(lpId) === String(p.id))
                      );

                      return (
                        <tr key={team.id} className="brio-row--hoverable">
                          <td>
                            <div className="brio-flex brio-items-center brio-gap-xs">
                              <BrioTeamIcon
                                color={team.color}
                                size={24}
                                ring={4}
                                title="Team Color (used in resource allocation)"
                              />
                              <div>
                                <div className="brio-font-bold">{team.name}</div>
                                <div className="brio-text-xs brio-text-grey brio-mt-xs">
                                  {linkedProjects.length > 0
                                    ? linkedProjects.map(p => p.name).join(', ')
                                    : 'No projects linked'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="brio-text-grey">
                              {supervisors.length > 0 ? supervisors.map(s => s.name).join(', ') : '—'}
                            </span>
                          </td>
                          <td>
                            <div>
                              <span className="brio-badge brio-badge--pending brio-mb-xs">
                                {hfbLoading ? '...' : teamMembers.length} {teamMembers.length === 1 ? 'member' : 'members'}
                              </span>
                              <div className="brio-text-xs brio-text-grey brio-mt-xs">
                                {hfbLoading
                                  ? 'Loading assignments...'
                                  : teamMembers.length > 0
                                    ? teamMembers.map(m => m.name).join(', ')
                                    : 'No members assigned this week'}
                              </div>
                            </div>
                          </td>
                          <td className="text-center">
                            <span className="brio-font-bold">
                              {hfbLoading ? '...' : teamMembers.length}
                            </span>
                          </td>
                          {canManageTeams && (
                          <td className="text-center">
                            <BrioActionGroup center>
                              <BrioIconButton
                                icon="edit"
                                variant="edit"
                                title="Edit team"
                                onClick={() => {
                                  // Get current project IDs linked to this team from team_projects join table
                                  const teamProjectIds = supabaseTeamProjects
                                    ? supabaseTeamProjects.getProjectIdsForTeam(team.id)
                                    : [];
                                  setForm({
                                    editTeamId: team.id,
                                    teamName: team.name,
                                    teamColor: team.color,
                                    teamSupervisors: team.supervisorIds || [],
                                    teamProjects: teamProjectIds || []
                                  });
                                  setModal('editTeam');
                                }}
                              />
                            </BrioActionGroup>
                          </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </BrioTable>
              </BrioByCode>
            </BrioAnalyticsSurface>
          ) : (
            <BrioAnalyticsSurface padding="m">
              <div className="brio-text-center brio-analytics-muted" style={{ padding: '48px 0' }}>
                No teams added yet
              </div>
            </BrioAnalyticsSurface>
          )}
        </div>
      </BrioFrame>
    </div>
  );
}
